import React from "react";

import Reveal, { useReveal } from "../motion/Reveal";
import { Chip } from "../ui/primitives";
import { CONSOLE } from "../../content/console";

// The home page's one piece of imagery. Rather than a stock photo or an
// abstract gradient, the hero shows the shape of the thing we actually build:
// a queue with stages, jobs and VRAM.
//
// Motion here is confined to what a real console would do on its own: the
// active markers breathe, and rows arrive in sequence when the panel first
// comes into view. Nothing counts up, because nothing here is live and
// pretending otherwise would be a lie told in pixels.

const MARKER = {
  active: "bg-accent animate-pulse-dot",
  done: "bg-fg-muted",
  idle: "bg-line",
};

const STAGE_TEXT = {
  active: "text-fg",
  done: "text-fg-secondary",
  idle: "text-fg-muted",
};

function Cell({ column, children, className = "" }) {
  return <div className={`${column.width} shrink-0 ${className}`}>{children}</div>;
}

export default function HeroConsole() {
  const [ref, shown] = useReveal();

  return (
    <section className="shell pb-20 pt-7 lg:pb-[104px]">
      <Reveal>
        <div ref={ref} className="console">
          {/* Bar */}
          <div className="flex items-center justify-between gap-4 border-b border-line px-5 py-3.5">
            <div className="flex items-center gap-2.5">
              <span className="h-1.5 w-1.5 shrink-0 animate-pulse-dot bg-accent" aria-hidden="true" />
              <span className="font-mono text-[12.5px] text-fg-secondary">{CONSOLE.title}</span>
            </div>
            <div className="hidden items-center gap-[22px] sm:flex">
              {CONSOLE.meta.map((m) => (
                <span key={m} className="font-mono text-[11.5px] tracking-[0.4px] text-fg-muted">
                  {m}
                </span>
              ))}
            </div>
          </div>

          <div className="flex">
            {/* Stages. Dropped below lg: the job table is the informative half. */}
            <div className="hidden w-[300px] shrink-0 flex-col gap-3 border-r border-line px-5 pb-[22px] pt-[18px] lg:flex">
              <span className="font-caption text-[10.5px] uppercase tracking-[1.6px] text-fg-muted">
                {CONSOLE.stagesLabel}
              </span>
              <div className="flex flex-col">
                {CONSOLE.stages.map((s, i) => (
                  <div
                    key={s.name}
                    className="flex items-center gap-[11px] py-[9px] transition-all duration-500"
                    style={{
                      opacity: shown ? 1 : 0,
                      transform: shown ? "none" : "translateX(-6px)",
                      transitionDelay: `${140 + i * 60}ms`,
                    }}
                  >
                    <span className={`h-[5px] w-[5px] shrink-0 rounded-full ${MARKER[s.tone]}`} />
                    <span className={`flex-1 font-mono text-[13px] ${STAGE_TEXT[s.tone]}`}>{s.name}</span>
                    <span className="font-mono text-[11px] text-fg-muted">{s.state}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Job table. Scrolls inside itself rather than pushing the page. */}
            <div className="min-w-0 flex-1 overflow-x-auto">
              <div className="min-w-[620px]">
                <div className="flex border-b border-line px-5 py-[13px]">
                  {CONSOLE.columns.map((c) => (
                    <Cell key={c.key} column={c}>
                      <span className="font-caption text-[10.5px] uppercase tracking-[1.4px] text-fg-muted">
                        {c.label}
                      </span>
                    </Cell>
                  ))}
                </div>

                {CONSOLE.rows.map((row, i) => (
                  <div
                    key={row.job}
                    className="flex items-center px-5 py-[13px] transition-all duration-500 last:border-b-0
                               [&:not(:last-child)]:border-b [&:not(:last-child)]:border-line-subtle"
                    style={{
                      opacity: shown ? 1 : 0,
                      transform: shown ? "none" : "translateY(8px)",
                      transitionDelay: `${200 + i * 70}ms`,
                    }}
                  >
                    <Cell column={CONSOLE.columns[0]}>
                      <span className="font-mono text-[12.5px] text-fg-secondary">{row.job}</span>
                    </Cell>
                    <Cell column={CONSOLE.columns[1]}>
                      <span className="font-mono text-[12.5px] text-fg-secondary">{row.base}</span>
                    </Cell>
                    <Cell column={CONSOLE.columns[2]}>
                      <span className="font-mono text-[12.5px] text-fg-body">{row.stage}</span>
                    </Cell>
                    <Cell column={CONSOLE.columns[3]}>
                      <span className="font-mono text-[12.5px] text-fg-secondary">{row.vram}</span>
                    </Cell>
                    <Cell column={CONSOLE.columns[4]}>
                      <Chip tone={row.tone}>{row.state}</Chip>
                    </Cell>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </Reveal>
    </section>
  );
}
